import { JSONValue } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useState } from 'react';
import { DialogTrigger, Disclosure, DisclosurePanel } from 'react-aria-components';

import { merge } from '@/components';
import {
  Button,
  Dialog,
  Flex,
  Heading,
  Label,
  Modal,
  ModalOverlay,
  Text,
  TextArea,
  TextField,
} from '@/components/core';
import { CheckmarkIcon, ChevronIcon, RotateIcon, SaveIcon } from '@/components/icons';
import { useGetAgentFromPath } from '@/hooks';
import { toActionRunId, useCreateActionRun, useGetActionRun } from '@/hooks/api';
import { useTriggerStore } from '@/store';
import { FieldStateValueType, findDocumentId, findModelId, isArray } from '@/utils';

import { useGetSamplePayloads } from './useGetSamplePayloads';

const toFieldValue = (v: JSONValue): FieldStateValueType | undefined => v as FieldStateValueType | undefined;

export type ActionRunTestProps = {
  label?: string;
};

const IActionRunTest = ({ label }: ActionRunTestProps) => {
  const action = useTriggerStore((state) => state.action);
  const [runId, setRunId] = useState<string | null>(null);
  const [selectedPayloadIndex, setSelectedPayloadIndex] = useState<number | undefined>(undefined);

  const { data: agent } = useGetAgentFromPath();
  const { runs, payloads, refetch, isPending: isPendingRefetch } = useGetSamplePayloads(agent?.agentId);
  const { mutateAsync: createActionRun, isPending } = useCreateActionRun();
  const { data: actionRun } = useGetActionRun(toActionRunId(action.actionId, runId), {
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 2000 : undefined),
  });

  const onTestActionRun = useCallback(async () => {
    setRunId(null);

    try {
      if (selectedPayloadIndex == null) return;

      const modelId = findModelId(agent);
      const run = runs?.[selectedPayloadIndex];
      const agentRunId = run?.id;
      const documentId = findDocumentId(run);

      if (modelId && agentRunId && documentId && action.actionId) {
        const { runId } = await createActionRun({
          actionId: action.actionId,
          input: { documentId, modelId },
          agentRunId,
          noAgentRunUpdate: true,
        });

        setRunId(runId);
      }
    } catch (error) {
      console.error('ActionRun test failed:', error);
    }
  }, [agent, selectedPayloadIndex, action.actionId, createActionRun, runs]);

  const onCloseTestModal = useCallback(() => {
    setRunId(null);
    setSelectedPayloadIndex(undefined);
  }, []);

  return (
    <DialogTrigger onOpenChange={onCloseTestModal}>
      <Button className="w-fit self-end" isDisabled={!action?.config.url} variant="primary">
        <Text>{label ?? 'Test webhook'}</Text>
      </Button>

      <ModalOverlay>
        <Modal>
          <Dialog className="gap-md flex w-[35rem] flex-col p-4 outline-none">
            <Heading size="lg">Test Action</Heading>

            <div className="flex items-center">
              <Text>Select a payload to send to the webhook for testing.</Text>
              <div className="grow" />
              <Button variant="plain" isDisabled={isPendingRefetch}>
                <RotateIcon
                  className={merge(isPendingRefetch && 'animate-spin')}
                  rotation="cw"
                  onClick={() => refetch()}
                />
              </Button>
            </div>

            <div>
              {payloads.length > 0 ? (
                <Flex col className="gap-sm">
                  {payloads.map((payload, index) => (
                    <Flex
                      key={index}
                      className={`px-md py-sm cursor-pointer rounded-lg border ${
                        selectedPayloadIndex === index
                          ? 'border-primary-500 bg-primary-100'
                          : 'border-gray-300 bg-white'
                      }`}
                      onClick={() => setSelectedPayloadIndex(index)}
                    >
                      <Text className="truncate">
                        {Object.values(payload as object)
                          ?.map((v) => (isArray(v) ? toFieldValue(v[0])?.value : toFieldValue(v)?.value || v))
                          ?.filter((v) => v != null)
                          ?.slice(0, 3)
                          ?.join(', ')}
                        ...
                      </Text>
                      <div className="grow" />
                      {selectedPayloadIndex === index && <CheckmarkIcon variant="success" />}
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Text>No sample payloads available.</Text>
              )}
            </div>

            {selectedPayloadIndex != null && (
              <TextField value={JSON.stringify(payloads[selectedPayloadIndex], null, 2)}>
                <Label>Selected Payload</Label>
                <TextArea readOnly className="min-h-[7rem] w-full rounded-lg border-gray-300" />
              </TextField>
            )}

            <Flex className="gap-sm justify-end">
              <Button variant="secondary" slot="close">
                <Text>Cancel</Text>
              </Button>
              <Button
                variant="primary"
                onPress={onTestActionRun}
                isDisabled={!action?.config.url || actionRun?.status === 'running' || isPending}
              >
                {actionRun?.status === 'running' || isPending ? <SaveIcon /> : null}
                <Text>Run test</Text>
              </Button>
            </Flex>

            {actionRun && actionRun.status !== 'running' && (
              <Disclosure className="group/webhook-test">
                <Button
                  slot="trigger"
                  className="items-center bg-transparent p-0"
                  variant={actionRun.status === 'succeeded' ? 'success' : 'error'}
                >
                  <ChevronIcon rotation="down" className="-rotate-90 group-data-[expanded]/webhook-test:rotate-0" />
                  <Heading size="xs">
                    {actionRun.status === 'succeeded'
                      ? 'Success!'
                      : `Failed with status code ${actionRun?.output?.statusCode}.`}
                  </Heading>
                </Button>
                <DisclosurePanel>
                  <TextArea
                    className="max-h-[8rem] w-full border-none bg-gray-50"
                    value={
                      actionRun?.output?.body
                        ? JSON.stringify(actionRun?.output?.body, null, 2)
                        : 'Empty response body.'
                    }
                  />
                </DisclosurePanel>
              </Disclosure>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};

export const ActionRunTest = memo(IActionRunTest) as typeof IActionRunTest;
